import { ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import posthog from 'posthog-js'
import { AlertCircle } from 'lucide-react'
import { useCurrentUser } from '../../hooks/api/useUsers'
import { useAuth } from './AuthProvider'
import { ApiError } from '../../api/client'
import { Button } from '../ui/Button'

/**
 * Renders the authenticated app only once the current user's profile has
 * loaded. A valid Supabase session (isSignedIn) is not enough: the app needs
 * the user's identity, tenant and roles from /users/me. If that fetch fails
 * (network, CORS, 401, 403...), show an explicit, actionable error instead of
 * the old silent fallback (a fake "utilisateur@example.com" user over empty
 * pages). The failure is also reported to PostHog, which otherwise never sees
 * a React-Query-handled error.
 */
export function AppBootstrapGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation('auth')
  const { signOut } = useAuth()
  const { data: user, isLoading, isError, error, refetch, isFetching } = useCurrentUser()

  useEffect(() => {
    if (!isError || !error) return
    const status = error instanceof ApiError ? error.status : undefined
    try {
      posthog.captureException(error, {
        context: 'auth_gate_profile_load_failed',
        status,
        endpoint: '/api/users/me',
      })
    } catch {
      // PostHog disabled / not initialized: silent no-op
    }
  }, [isError, error])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 rounded-full border-4 border-muted border-t-primary animate-spin" />
          <p className="text-13 text-muted-foreground">{t('loadingProfile')}</p>
        </div>
      </div>
    )
  }

  if (isError || !user) {
    const status = error instanceof ApiError ? error.status : undefined
    const isOrgError = status === 403
    const isAuthError = status === 401
    const message = isOrgError
      ? t('gate.messageOrg')
      : isAuthError
        ? t('gate.messageAuth')
        : t('gate.messageGeneric')

    const retryButton = (
      <Button variant={isOrgError || isAuthError ? 'outline' : 'default'} onClick={() => { void refetch() }} loading={isFetching}>
        {t('gate.retry')}
      </Button>
    )
    const signOutButton = (
      <Button variant={isOrgError || isAuthError ? 'default' : 'outline'} onClick={() => { void signOut() }}>
        {t('gate.signOut')}
      </Button>
    )

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="flex flex-col items-center gap-4 text-center max-w-sm">
          <AlertCircle className="h-10 w-10 text-muted-foreground" />
          <div className="flex flex-col gap-1">
            <h1 className="text-foreground font-medium">{t('gate.title')}</h1>
            <p className="text-13 text-muted-foreground">{message}</p>
          </div>
          <div className="flex items-center gap-2">
            {isOrgError || isAuthError
              ? (<>{signOutButton}{retryButton}</>)
              : (<>{retryButton}{signOutButton}</>)}
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
